import React, { useEffect, useState, useCallback } from 'react';
import FlowsheetService from '../../services/flowsheetService';
import FlowsheetTable from './FlowsheetTable';
import { ToggleButton, ToggleButtonGroup, Box, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import TimelineIcon from '@mui/icons-material/Timeline';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const dateRanges = [
  { label: 'Today', value: 'today' },
  { label: 'Next 7 Days', value: '7days' },
  { label: 'Next 30 Days', value: '30days' },
  { label: 'Next 6 Months', value: '6months' },
];

function isInRange(date, range) {
  const now = new Date();
  const d = new Date(date);
  if (range === 'today') {
    return d.toDateString() === now.toDateString();
  }
  if (range === '7days') {
    const end = new Date(now); end.setDate(now.getDate() + 7);
    return d >= now && d <= end;
  }
  if (range === '30days') {
    const end = new Date(now); end.setDate(now.getDate() + 30);
    return d >= now && d <= end;
  }
  if (range === '6months') {
    const end = new Date(now); end.setMonth(now.getMonth() + 6);
    return d >= now && d <= end;
  }
  return true;
}

const NursingFlowsheet = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [pageSize, setPageSize] = useState(10);

  // Fetch all flowsheet entries for all in-progress inpatient encounters
  const fetchFlowsheetRows = useCallback(async () => {
    setLoading(true);
    try {
      const flowsheetService = new FlowsheetService();
      const result = await flowsheetService.getFlowsheetsWithPatientData();
      setRows(result.flowsheets || []);
    } catch (err) {
      console.error('Error fetching flowsheet rows:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlowsheetRows(); }, [fetchFlowsheetRows]);

  // Filtered rows for DataGrid
  const filteredRows = rows.filter(r => r._timeObj && isInRange(r._timeObj, dateFilter));

  return (
    <Box sx={{ p: 2 }}>
      {/* Purple/Blue Gradient Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Nursing Flowsheet
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage patient flowsheets and nursing documentation
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <ToggleButtonGroup
          value={dateFilter}
          exclusive
          onChange={(_, v) => { if (v) setDateFilter(v); }}
          sx={{ borderRadius: 999, background: '#f5f5f5', p: 0.5 }}
        >
          {dateRanges.map(r => (
            <ToggleButton
              key={r.value}
              value={r.value}
              sx={{ borderRadius: 999, px: 2, fontWeight: 600, textTransform: 'none' }}
            >
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <FlowsheetTable
        rows={filteredRows}
        loading={loading}
        onRefresh={fetchFlowsheetRows}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />
    </Box>
  );
};

export default NursingFlowsheet; 